// Translated
// Migrated
<template>
  <div data-test-id="CalendarSearchBar">
    <div
      ref="searchcontainer"
      class="charter-search package-search calendar overflow-hidden"
      :class="{ sv: sv }"
    >
      <CalendarSearchBarButtonAirport
        :active="activeSearch === SECTIONS.airport"
        :select-airport-first="selectAirportFirst"
        :loading="!initialized"
        @set-active="setActiveSearch(SECTIONS.airport)"
      />
      <CalendarSearchBarButtonStopover
        :active="activeSearch === SECTIONS.stopover"
        :loading="!initialized"
        @set-active="setActiveSearch(SECTIONS.stopover)"
      />
      <CalendarSearchBarButtonDuration
        :active="activeSearch === SECTIONS.duration"
        :selected-duration="selectedDuration"
        :loading="!initialized"
        @set-active="setActiveSearch(SECTIONS.duration)"
      />
      <CharterSearchButtonGuests
        v-if="!sv"
        ref="charterButtonGuests"
        :active="activeSearch === SECTIONS.guests"
        :display-rooms="peoplePerRoom"
        :select-child-ages="selectChildAges"
        :loading="!initialized"
        @set-active="setActiveSearch(SECTIONS.guests)"
      />
    </div>
    <div
      id="charter-parent"
      class="relative"
    >
      <CharterAirportSelect
        key="charterWindowAirport"
        ref="charterWindowAirport"
        :active="activeSearch === SECTIONS.airport"
        :prefill="prefill"
        :airport-options="airportList"
        :selected-airport="selectedAirport"
        :select-airport-first="selectAirportFirst"
        :prefilled-selected-airport="selectedAirport"
        @close="setActiveSearch(null, true)"
        @select="selectAirport"
      />
      <LazyCalendarSearchBarStopoverWindow
        v-show="activeSearch === SECTIONS.stopover"
        ref="charterWindowStopover"
        key="charterWindowStopover"
        :stopovers="stopovers"
        @close="setActiveSearch(null, true)"
        @select="selectStopovers"
      />
      <LazyCalendarSearchBarDurationWindow
        ref="charterWindowDuration"
        :key="`duration-window-${selectedAirport?.airport_name}`"
        :active="activeSearch === SECTIONS.duration"
        @clear="setActiveSearch(null, true)"
        @update="selectDuration"
        @search="searchCalendar"
      />
      <LazyCharterSearchWindowGuests
        v-show="activeSearch === SECTIONS.guests"
        id="charterWindowGuests"
        ref="charterWindowGuests"
        key="charterWindowGuests"
        :rooms="rooms"
        :passengers-full="overMaxNoOfPassengers"
        @clear="setActiveSearch(null, true)"
        @update="updateRooms"
      />
    </div>
    <LazyCharterBookingErrorModal
      :errors="tooManyInfants"
      :no-close-on-esc="false"
      :close-self="true"
    >
      <template #text>
        <span data-i18n="charterSearchTooManyInfants">
          {{ $t('charterSearchTooManyInfants') }}
        </span>
      </template>
    </LazyCharterBookingErrorModal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'

import { DEFAULT_CUSTOM_DURATION } from '@layers/web/stores/dealfinder'

const SECTIONS = {
  airport: 'airport',
  stopover: 'stopover',
  duration: 'duration',
  guests: 'guests',
}
export default defineNuxtComponent({
  name: 'CalendarSearchBar',

  emits: ['initialized', 'update', 'duration', 'onSetActiveSearch'],

  data () {
    return {
      SECTIONS,

      selectedDuration: DEFAULT_CUSTOM_DURATION,

      activeSearch: null,
      selectAirportFirst: false,
      selectChildAges: false,
      tooManyInfants: false,

      window: 0,

      timer: null,

      initialized: false,

      prefill: {},
    }
  },

  computed: {
    ...mapState(useRootStore, ['charterAirports']),

    ...mapState(useDealfinderStore, {
      selectedAirport: 'selectedAirport',
      airports: 'airportList',
      stopovers: 'stopovers',
    }),

    ...mapState(useCharterPackagesStore, {
      overMaxNoOfPassengers: 'overMaxNoOfPassengers',
      peoplePerRoom: 'peoplePerRoom',
      selectedStopovers: 'filterStopover',
      rooms: 'rooms',
    }),

    ...mapState(useLocaleStore, {
      sv: 'isRolfSwe',
    }),

    // map full option by selected triplengths
    airportList () {
      const list = (this.airports || [])
        .map(x => ({
          ...x,
          full: (this.charterAirports || []).find(y => y?.airport === x?.airport),
        }))
        .filter(x => !!x?.full)
        .map(({ full, ...item }) => ({
          ...item,
          ...full,
        }))

      return list
    },
  },

  async beforeMount () {
    await this.getDestinations()
    await this.setStagedAirport(this.selectedAirport)

    await this.prefillSearchWidgetDestinations(this.prefill)
    await this.getFilters()

    await this.searchCalendar()

    this.initialized = true
    this.$emit('initialized', true)
  },

  mounted () {
    this.window = window.innerWidth
    window.addEventListener('resize', this.resizeListener)
  },

  beforeUnmount () {
    clearTimeout(this.timer)
    window.removeEventListener('resize', this.resizeListener)
  },

  methods: {
    ...mapActions(useCharterPackagesStore, {
      setRooms: 'SET_ROOMS',
      setStopovers: 'SET_FILTER_STOPOVER',
      getFilters: 'getFilters',
      getDestinations: 'getDestinations',
    }),

    ...mapActions(useSearchWidgetsStore, {
      setStagedAirport: 'SET_STAGED_AIRPORT',
      prefillSearchWidgetDestinations: 'prefillDestinations',
    }),

    ...mapActions(useDealfinderStore, {
      setAirportDealfinder: 'SELECT_AIRPORT',
      setDuration: 'SELECT_DURATION',
      setDurationsWithOverrides: 'setDurationsWithOverrides',
      search: 'search',
    }),

    ...mapActions(useRootStore, {
      setGlobalAirport: 'setSelectedAirport',
    }),

    async selectAirport ([airport]) {
      if (
        !airport ||
        airport?.airport === this.selectedAirport?.airport ||
        airport === this.selectedAirport
      ) {
        return
      }

      this.setGlobalAirport(airport.airport)
      this.setAirportDealfinder(airport)
      await this.setStagedAirport(airport)

      this.setDurationsWithOverrides()

      await this.updateStopovers(Math.min(...(
        airport?.stopovers?.length
          ? airport.stopovers
          : [0]
      )))

      this.setActiveSearch(null)

      await this.searchCalendar()
    },

    async searchCalendar () {
      await this.search()
      this.$emit('update')
      this.$emit('duration', this.selectedDuration)

      await this.updateStopovers(this.selectedStopovers)
    },

    resizeListener () {
      this.window = window.innerWidth
    },

    setActiveSearch (search, shouldRefresh) {
      this.activeSearch = this.activeSearch === search ? null : search
      this.selectAirportFirst = false

      // When closing by clicking on the dropdown again
      if (shouldRefresh || (!this.activeSearch && search)) {
        this.searchCalendar()
      }

      this.$emit('onSetActiveSearch', this.activeSearch)
    },

    async selectStopovers (stops) {
      await this.updateStopovers(stops)
      this.setActiveSearch(null)
      await this.searchCalendar()
    },

    async updateStopovers (stops) {
      if (stops === this.filterStopover) {
        stops = stops > 0 ? stops - 1 : 2
      }

      await this.updateQuery({ stopover: stops })
      await this.setStopovers(stops)
    },

    selectDuration (v) {
      this.selectedDuration = v
      this.setDuration(v)
      if (v?.type !== 'custom') {
        this.setActiveSearch(null)
      }
    },

    updateRooms ({ rooms, emptyAgeValues, tooManyInfants }) {
      this.setRooms(rooms)
      this.selectChildAges = emptyAgeValues
      this.tooManyInfants = tooManyInfants
    },

    async updateQuery (q = {}) {
      const current = this.$router.currentRoute.value

      const currentKeys = Object.keys(current?.query)
      const query = currentKeys
        .reduce((b, key) => ({
          ...b,
          [key]: q?.[key] !== undefined ? q?.[key] : current?.query?.[key],
        }), {})

      await this.$router.replace({
        ...current,
        query,
      }).catch(() => {})
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/charter/_charter-forms";
@import "@layers/web/assets/scss/modules/charter/_charter-filter";

.blink {
  animation: blink 1.22s;
  background: theme('colors.orange-transparent');

  &-title {
    background: transparent;
  }
}

@keyframes blink {
  0% {
    background: transparent;
  }

  20%,
  80% {
    background: theme('colors.orange.DEFAULT');
  }

  50% {
    background: theme('colors.orange-transparent');
  }
}

#charterWindowAirport {
  &.sm .row {
    & > div:first-of-type,
    & > div:last-of-type {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  &.md .row {
    & > div:first-of-type {
      flex: 1 0 33.33333;
      max-width: 33.33333%;
    }
    & > div:last-of-type {
      flex: 1 0 66.66666%;
      max-width: 66.66666%;
    }
  }

  &.lg .row,
  &.xl .row {
    & > div:first-of-type {
      flex: 1 0 25%;
      max-width: 25%;
    }
    & > div:last-of-type {
      flex: 1 0 75%;
      max-width: 75%;
    }
  }
}
</style>
